import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { decode } from 'he';
import api from '../services/apiService';
import { usePerms } from '../hooks/useAuth';
import HpExportStatus from './HpExportStatus';
import './ItemWebChannelHpEdit.css';

const empty = { isActive: false, conditionId: '', titleOverride: '', descriptionOverride: '', location: '', imageUrl: '', notes: '',
  netPrice: '', netPricePerHour: '', netPricePerDay1: '', netPricePerDay2: '', netPricePerDay3: '', netPricePerDay4: '' };
const plain = value => decode(String(value ?? '').replace(/<br\s*\/?\s*>|<\/p>/gi, '\n').replace(/<[^>]*>/g, '')).trim();
const prices = [['netPrice', 'Verkaufspreis netto (EUR)'], ['netPricePerHour', 'Mietpreis pro Stunde netto (EUR)'],
  ...[1, 2, 3, 4].map(tier => ['netPricePerDay' + tier, `Mietpreis pro Tag – Preisstufe ${tier} netto (EUR)`])];

export default function ItemWebChannelHpEdit() {
  const { itemId } = useParams();
  const { has } = usePerms();
  const [article, setArticle] = useState({});
  const [conditions, setConditions] = useState([]);
  const [values, setValues] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setMessage(''); setValues(empty); setArticle({});
    const load = async () => {
      try {
        const [item, conditionList, channels] = await Promise.all([api.get(`/items/${itemId}`), api.get('/conditions'), api.get(`/item-web-channels/item/${itemId}`)]);
        if (!active) return;
        setArticle(item.data); setConditions(conditionList.data || []);
        const hp = channels.data.find(channel => channel.channelKey === 'HP');
        if (hp) setValues(Object.fromEntries(Object.entries(empty).map(([key, fallback]) => [key, hp[key] ?? fallback])));
      } catch (err) { if (active) setError(err.response?.data?.error || 'HP-Web-Channel konnte nicht geladen werden.'); }
      finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [itemId]);
  const change = (key, value) => setValues(current => ({ ...current, [key]: value }));
  const save = async event => {
    event.preventDefault(); setSaving(true); setError(''); setMessage('');
    try {
      const response = await api.post(`/item-web-channels/item/${itemId}`, { ...values, channelKey: 'HP' });
      setMessage(`Gespeichert. Die vollständige JSON-Datei enthält ${response.data.export.itemCount} Maschinen (Stand ${response.data.export.revision}).`);
      setRefresh(value => value + 1);
    } catch (err) { setError(err.response?.data?.error || 'HP-Web-Channel und JSON-Datei konnten nicht gespeichert werden.'); }
    finally { setSaving(false); }
  };
  if (loading) return <div>Lade HP-Web-Channel …</div>;
  const title = [article.manufacturer, article.manufacturerType].map(plain).filter(Boolean).join(' - ') || plain(article.name);
  return <div className="hp-channel-editor">
    <Helmet><title>HP Web-Channel bearbeiten</title></Helmet>
    <h2>HP Web-Channel bearbeiten</h2>
    <p>Artikel {itemId} · {article.name}</p>
    <p>
      {has('/itemWebChannelListHP') && <Link to="/itemWebChannelListHP">Zur HP-Liste</Link>}
      {has('/hp-integration') && <>{has('/itemWebChannelListHP') && ' · '}<Link to="/hp-integration">HP-Verbindung und Exportstatus</Link></>}
    </p>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    <form onSubmit={save}>
      <div className="form-row">
        <div className="col">
          <label>Zustand<select value={values.conditionId} onChange={event => change('conditionId', event.target.value)}>
            <option value="">Zustand aus Artikel übernehmen</option>
            {conditions.map(condition => <option key={condition.id} value={condition.id}>{condition.name}</option>)}
          </select></label>
          <label>Standort<input value={values.location} maxLength={200} onChange={event => change('location', event.target.value)}/></label>
          <label>Titel<input value={values.titleOverride} maxLength={200} placeholder={title} onChange={event => change('titleOverride', event.target.value)}/></label>
          <p>Leer lassen übernimmt Hersteller und Typ oder den Artikelnamen.</p>
          <label>Beschreibung<textarea value={values.descriptionOverride} maxLength={20000} rows={8} placeholder={plain(article.description)} onChange={event => change('descriptionOverride', event.target.value)}/></label>
          <p>Leer lassen übernimmt die Artikelbeschreibung.</p>
        </div>
        <div className="col">
          {prices.map(([key, label]) => <label key={key}>{label}<input type="text" inputMode="decimal" value={values[key]} onChange={event => change(key, event.target.value)} placeholder="Preis auf Anfrage"/></label>)}
          <p>Leere Preisfelder werden als „Preis auf Anfrage“ exportiert. Die Preisstufen entsprechen euren vereinbarten Mietpreisstaffeln.</p>
        </div>
        <div className="col">
          <div className="hp-channel-publication"><label>
            <input type="checkbox" checked={values.isActive} onChange={event => change('isActive', event.target.checked)}/>
            Auf Homepage veröffentlichen
          </label></div>
          {article.deactivated && <p>Der Artikel ist im Artikelstamm deaktiviert und erscheint daher nicht in der Exportdatei.</p>}
          <label>Abweichende Bildadresse<input value={values.imageUrl} maxLength={255} placeholder={article.picHP || 'Bildadresse aus Artikelstamm (picHP)'} onChange={event => change('imageUrl', event.target.value)}/></label>
          <p>Leer lassen verwendet das gemeinsame Bild aus dem Artikelstamm.</p>
          <label>Öffentliche Ausstattung / Hinweise<textarea value={values.notes} maxLength={10000} rows={5} onChange={event => change('notes', event.target.value)}/></label>
        </div>
      </div>
      <p>Bei jedem Speichern wird die JSON-Datei mit allen für HP freigegebenen Maschinen vollständig neu erstellt.</p>
      <button type="submit" className="submit-button" disabled={saving || !article.id}>{saving ? 'Speichert …' : 'HP-Web-Channel speichern'}</button>
    </form>
    <HpExportStatus refresh={refresh}/>
  </div>;
}
